import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, limit, query, startAfter, orderBy } from 'firebase/firestore';
import { ArrowRight, ShoppingCart } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import DynamicRating from './DynamicRating';
import CardWishlistButton from '../../components/CardWishlistButton';

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const { addToCart } = useCart();

  const PRODUCTS_PER_PAGE = 4;

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'products'), 
          orderBy('name'), 
          limit(PRODUCTS_PER_PAGE)
        );
        const querySnapshot = await getDocs(q);
        
        const productsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setFeaturedProducts(productsList);
        
        const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastVisible(lastDoc);

        if (querySnapshot.docs.length < PRODUCTS_PER_PAGE) {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching featured products: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, []);

  const fetchMoreProducts = async () => {
    if (!lastVisible || loadingMore || !hasMore) return;

    setLoadingMore(true);

    setTimeout(async () => {
      try {
        const q = query(
          collection(db, 'products'),
          orderBy('name'),
          startAfter(lastVisible),
          limit(PRODUCTS_PER_PAGE)
        );
        
        const querySnapshot = await getDocs(q);
        
        const newProductsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        if (newProductsList.length > 0) {
          setFeaturedProducts(prev => [...prev, ...newProductsList]);
          const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
          setLastVisible(lastDoc);
        }

        if (querySnapshot.docs.length < PRODUCTS_PER_PAGE) {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching more products: ", error);
      } finally {
        setLoadingMore(false);
      }
    }, 3000); 
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300) {
        fetchMoreProducts();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastVisible, loadingMore, hasMore]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      
      {/* 1. Amazon-Style Top Sub-Navigation Bar */}
      <div className="bg-emerald-950 text-white text-xs py-2 px-4 sm:px-6 lg:px-8 flex items-center space-x-6 overflow-x-auto whitespace-nowrap shadow-inner">
        <Link to="/shop" className="font-bold hover:underline flex items-center space-x-1"><span>🌱 All Eco-Products</span></Link>
        <Link to="/shop" className="hover:underline">Bestselling Zero-Waste</Link>
        <Link to="/shop" className="hover:underline">Biodegradable Packaging</Link>
        <Link to="/shop" className="hover:underline">Ethically Sourced</Link>
        <Link to="/shop" className="hover:underline">Circular Economy Deals</Link>
        <Link to="/shop" className="hover:underline">Plant-a-Tree Initiative</Link>
      </div>

      {/* 2. Hero Banner Section */}
      <section className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-ecoGreen-dark text-white py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden shadow-md">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <span className="bg-emerald-700/80 border border-emerald-500 text-emerald-200 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">
              100% Sustainable Choice
            </span>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight mt-4 mb-3 leading-tight">
              Bestselling Eco-Essentials <span className="text-emerald-300">Under ₹399</span>
            </h1>
            <p className="text-xs sm:text-sm text-emerald-100 mb-6 font-light">
              Discover zero-waste goods and biodegradable daily utilities designed to reduce your carbon footprint instantly.
            </p>
            <Link
              to="/shop"
              className="bg-white text-emerald-950 font-extrabold px-6 py-3 rounded-xl shadow-lg hover:bg-emerald-50 transition-all inline-flex items-center space-x-2 text-xs sm:text-sm"
            >
              <span>Explore Collection</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-3xl text-center shadow-xl">
            <p className="text-xs uppercase tracking-widest text-emerald-200 font-bold mb-2">Special Green Offer</p>
            <div className="text-2xl font-black text-white">🌿 Zero Plastic Guarantee</div>
            <p className="text-xs text-emerald-100 mt-2">Free Delivery on all orders above ₹499 + 1 Tree Planted</p>
          </div>
        </div>
      </section>

      {/* 3. Amazon-Style Multi-Grid Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 mb-12 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 flex flex-col justify-between">
            <div>
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded">Upcoming Event</span>
              <h3 className="font-extrabold text-base text-gray-900 mt-2">Great Eco-Freedom Sale</h3>
              <p className="text-xs text-gray-500 mt-1">Massive discounts on all organic & compostable categories starting soon.</p>
            </div>
            <div className="mt-6 bg-gradient-to-br from-amber-500 to-orange-600 p-4 rounded-xl text-white text-center font-bold text-xs shadow-inner">
              Starts 7th August 🚀
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 flex flex-col justify-between">
            <div>
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded">Curated For You</span>
              <h3 className="font-extrabold text-base text-gray-900 mt-2">Deals on Sustainable Living</h3>
              <p className="text-xs text-gray-500 mt-1">Handpicked eco-friendly goods matching your recent browsing history.</p>
            </div>
            <Link to="/shop" className="mt-6 text-emerald-700 font-bold text-xs hover:underline flex items-center space-x-1">
              <span>See more deals</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 flex flex-col justify-between">
            <div>
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded">Clearance</span>
              <h3 className="font-extrabold text-base text-gray-900 mt-2">Up to 75% off | Zero-Waste Kits</h3>
              <p className="text-xs text-gray-500 mt-1">Upgrade your kitchen and home essentials with heavy price drops.</p>
            </div>
            <Link to="/shop" className="mt-6 text-emerald-700 font-bold text-xs hover:underline flex items-center space-x-1">
              <span>Explore discounts</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 flex flex-col justify-between">
            <div>
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded">B2B Eco</span>
              <h3 className="font-extrabold text-base text-gray-900 mt-2">Bulk Orders + Guaranteed Cashback</h3>
              <p className="text-xs text-gray-500 mt-1">Get special pricing for corporate gifting and bulk sustainable packaging.</p>
            </div>
            <Link to="/shop" className="mt-6 text-emerald-700 font-bold text-xs hover:underline flex items-center space-x-1">
              <span>Create business account</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Dynamic Featured Products Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full mb-16">
        <div className="flex justify-between items-end mb-6 border-b border-gray-200 pb-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">Featured Eco-Products</h2>
            <p className="text-xs text-gray-500">Loaded directly from your database inventory</p>
          </div>
          <Link to="/shop" className="text-emerald-700 font-bold text-xs hover:underline flex items-center space-x-1">
            <span>View All</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-16 font-medium text-sm">Loading database products...</div>
        ) : featuredProducts.length === 0 ? (
          <div className="bg-white p-10 rounded-2xl text-center shadow-sm border border-gray-200">
            <p className="text-gray-600 mb-3 text-xs font-medium">No products found in database yet.</p>
            <Link to="/admin" className="inline-block bg-emerald-800 text-white px-4 py-2 rounded-xl text-xs font-bold">
              Go to Admin Panel to Add Products
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-200 flex flex-col justify-between overflow-hidden group animate-fadeIn"
                >
                  <div>
                    <Link to={`/product/${product.id}`} className="block relative overflow-hidden bg-gray-100 h-48">
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.name} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        />
                      ) : (
                        <div className="w-full h-full bg-emerald-50 flex items-center justify-center text-emerald-800 font-bold text-xs">
                          🌿 {product.name}
                        </div>
                      )}
                      
                      {/* Compact & Professional Wishlist Button */}
                      <div className="absolute top-2 left-2 z-10 scale-90">
                        <CardWishlistButton product={product} />
                      </div>

                      <span className="absolute top-2.5 right-2.5 bg-white/90 backdrop-blur-md text-[9px] font-bold px-2 py-0.5 rounded-full text-gray-800 shadow-sm uppercase">
                        {product.category || 'Eco'}
                      </span>
                    </Link>

                    <div className="p-4">
                      <div className="mb-2 scale-90 origin-left">
                        <DynamicRating 
                          productId={product.id} 
                          initialRating={product.rating || 4.5} 
                          initialCount={product.reviewsCount || 10} 
                        />
                      </div>

                      <Link to={`/product/${product.id}`}>
                        <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-1 hover:text-emerald-700 transition-colors">
                          {product.name}
                        </h3>
                      </Link>

                      <p className="text-[11px] text-gray-500 mt-1 line-clamp-2 leading-relaxed">
                        {product.description || "Sustainable zero-waste alternative built for everyday utility."}
                      </p>

                      <div className="mt-3 flex items-baseline justify-between">
                        <span className="text-base font-black text-gray-900">₹{product.price}</span>
                        <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                          FREE Delivery
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 pt-0">
                    <button 
                      onClick={() => addToCart(product)}
                      className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-2 rounded-xl font-bold text-xs transition flex items-center justify-center space-x-1.5 shadow"
                    >
                      <ShoppingCart className="h-3.5 w-3.5" />
                      <span>Add to Cart</span>
                    </button>
                  </div>
                </div>
              ))}

              {loadingMore && (
                <>
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm animate-pulse flex flex-col justify-between h-80">
                      <div>
                        <div className="bg-gray-200 h-48 rounded-xl mb-4"></div>
                        <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
                        <div className="bg-gray-200 h-3 rounded w-1/2"></div>
                      </div>
                      <div className="bg-gray-200 h-8 rounded-xl w-full"></div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {!hasMore && featuredProducts.length > 0 && (
              <div className="text-center py-8 text-gray-400 text-xs">
                You've reached the end of the catalog! 🎉
              </div>
            )}
          </>
        )}
      </section>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
}